import Panel from 'components/Panel';
import FormikDropdown from 'components/inputs/FormikDropdown';
import {FormControlLabel, Radio, RadioGroup} from '@mui/material';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import FormikTextField from 'components/inputs/FormikTextField';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {ISearchCriteria, SearchCriteriaSchema, SearchMethod} from 'components/forms/SearchSchema';
import {SearchFieldType} from 'types/SearchFieldType';
import Typography from '@mui/material/Typography';
import FormikNumberField from 'components/inputs/FormikNumberField';
import Formik from 'components/hook-form/Formik';
import {useFieldValue} from 'components/hook-form/useFieldValue';
import {useFormContext} from 'react-hook-form';

type SearchMoveFormProps = {
   searchFieldsLabel: string;
   searchResult?: React.ReactNode;
   transaction?: boolean;
   onSubmit: (values: ISearchCriteria) => void;
};

const Form = ({
   searchFieldsLabel,
   searchResult,
   transaction = false,
}: Omit<SearchMoveFormProps, 'onSubmit'>) => {
   const {t} = useTranslation();
   const method = useFieldValue<ISearchCriteria>('method');
   const {setValue} = useFormContext<ISearchCriteria>();

   return (
      <Panel name={t('Search Criteria')}>
         <FormControl fullWidth component="fieldset">
            <Typography variant={'body2'} gutterBottom>
               {searchFieldsLabel}
            </Typography>
            <RadioGroup
               name={'method'}
               onChange={(event) => {
                  setValue('method', event.target.value as ISearchCriteria['method']);
               }}
            >
               <Grid container spacing={2} alignItems={'center'} wrap={'nowrap'}>
                  <Grid item xs={4}>
                     <FormControlLabel
                        value={SearchMethod.field}
                        control={<Radio />}
                        label={<Typography variant={'body2'}>{t('Search field')}</Typography>}
                     />
                  </Grid>
                  <Grid item xs={3}>
                     <FormikDropdown
                        name={'searchField'}
                        disabled={method !== SearchMethod.field}
                        options={SearchFieldType.options}
                     />
                  </Grid>
                  <Grid item style={{flex: '1 0'}}>
                     <FormikTextField
                        autoFocus
                        name="containingText"
                        label={t('containing phrase')}
                        disabled={method !== SearchMethod.field}
                     />
                  </Grid>
               </Grid>
               {!transaction ? (
                  <FormControlLabel
                     value={SearchMethod.unused}
                     control={<Radio />}
                     label={
                        <Typography variant={'body2'}>
                           {t('No referencing partners or transactions')}
                        </Typography>
                     }
                  />
               ) : (
                  <>
                     <Grid container spacing={2} alignItems={'center'}>
                        <Grid item xs={4}>
                           <FormControlLabel
                              value={SearchMethod.noProcessedFiles}
                              control={<Radio />}
                              label={
                                 <Typography variant={'body2'}>
                                    {t('No files processed for')}
                                 </Typography>
                              }
                           />
                        </Grid>
                        <Grid item xs={3}>
                           <FormikNumberField
                              name={'daysUnused'}
                              disabled={method !== SearchMethod.noProcessedFiles}
                              InputProps={{
                                 endAdornment: (
                                    <Typography variant={'body2'}>{t('days')}</Typography>
                                 ),
                              }}
                           />
                        </Grid>
                     </Grid>
                     <FormControlLabel
                        value={SearchMethod.noHistory}
                        control={<Radio />}
                        label={
                           <Typography variant={'body2'}>{t('No job history records')}</Typography>
                        }
                     />
                  </>
               )}
            </RadioGroup>
         </FormControl>
         <Grid container spacing={2} alignItems={'center'}>
            <Grid item>
               <PrimaryButton type={'submit'}>Search</PrimaryButton>
            </Grid>
            {searchResult ? (
               <Grid item>
                  <Typography variant={'body2'}>{searchResult}</Typography>
               </Grid>
            ) : null}
         </Grid>
      </Panel>
   );
};

const SearchMoveForm = ({onSubmit, ...props}: SearchMoveFormProps) => {
   return (
      <Formik
         initialValues={SearchCriteriaSchema.cast({searchField: 'NAME'})}
         validationSchema={SearchCriteriaSchema}
         onSubmit={onSubmit}
      >
         <Form {...props} />
      </Formik>
   );
};

export default SearchMoveForm;
